import Cookies from 'js-cookie';

import { augmentWithPrevTeam } from './auth0';

import { GDTApiErrors, UserInitialData } from '@/types';
import { TEXTS, axiosErrorHandler, getPlatformId } from '@/utils';
import axiosGdt, { setCurrentCsrfToken } from '@/utils/axiosGdt';
import { URLs } from '@/utils/urls';

/**
 * Kill-switch/opt-in flag, same convention as enableAuthenticatedLogging etc.
 * (see docs/Testing.md#5). Zonda is not wired up on the backend yet, so this
 * stays off by default:
 *   localStorage.setItem('enableZondaAuth', 'true'); // then reload
 */
export const enableZondaAuth =
  localStorage.getItem('enableZondaAuth') === 'true';

declare global {
  interface Window {
    zondaJS?: {
      init: (config: Record<string, unknown>) => void;
      login: () => Promise<void>;
      logout: (clearSession?: boolean) => Promise<void> | void;
      syncSession: (options: { forceLogin?: boolean }) => Promise<unknown>;
      getUserProfile: () => Record<string, unknown> | undefined;
      setupGoogleOneTap: () => void;
    };
  }
}

/**
 * apiKey is no longer hardcoded — it's fetched from the backend's configWM
 * response (Root.tsx calls rememberZondaConfig() once that resolves), same
 * pattern as clientConfig/rememberClient() in services/editProfile_pwdReset.ts.
 */
const zondaRemoteConfig: { apiKey?: string } = {};

export function rememberZondaConfig(config: { apiKey: string }) {
  zondaRemoteConfig.apiKey = config.apiKey;
}

/**
 * Everything else is still per-host, per the "Integración Gran DT" Confluence
 * page (id 4377772033, §7). GDT is a subdomain of clarin.com and inherits
 * Clarín's identity portal/callback ("AL SER UN SUBDOMINIO LO HEREDA") — the
 * callback below lives on Clarín's own domain, not in this repo.
 */
const ZONDA_QA_ENV_CONFIG = {
  routesBaseURL: 'https://contentaccessapi.qa.zondadevops.com/api/',
  identityURL: 'https://identity-zonda-qa-api.clarin.com/api/',
  identityCallbackURL: 'https://staging.clarin.com/zonda-callback',
};

/**
 * Deliberately broken placeholder — NOT a bug. grandt.clarin.com (production)
 * has no real Zonda config yet (Hiberus hasn't provided one), and any
 * unrecognized host shouldn't silently fall back to QA either: that would
 * risk production traffic hitting the QA Zonda tenant (or vice versa) if the
 * host-matching logic is ever wrong. `.invalid` is reserved by RFC 2606 to
 * always fail DNS resolution, so this fails loudly instead of quietly
 * talking to the wrong environment.
 */
const ZONDA_ENV_CONFIG_NOT_CONFIGURED = {
  routesBaseURL: 'https://zonda-prod-not-configured.invalid/api/',
  identityURL: 'https://zonda-prod-not-configured.invalid/api/',
  identityCallbackURL:
    'https://zonda-prod-not-configured.invalid/zonda-callback',
};

// TODO: grandt.clarin.com (production) needs its own real Zonda config
// (identityURL/identityCallbackURL/routesConfig.baseURL) once Hiberus
// provides it — until then it fails closed rather than falling back to QA.
const ZONDA_ENV_CONFIG_BY_HOST: Record<string, typeof ZONDA_QA_ENV_CONFIG> = {
  'desagrandt.clarin.com': ZONDA_QA_ENV_CONFIG,
  'testgrandt.clarin.com': ZONDA_QA_ENV_CONFIG,
  'grandtpre.clarin.com': ZONDA_QA_ENV_CONFIG,
  'grandt.clarin.com': ZONDA_ENV_CONFIG_NOT_CONFIGURED,
};

function getZondaConfig() {
  const envConfig =
    ZONDA_ENV_CONFIG_BY_HOST[window.location.hostname] ??
    ZONDA_ENV_CONFIG_NOT_CONFIGURED;

  return {
    apiKey: zondaRemoteConfig.apiKey,
    provider: 'ZONDA',
    storagePrefix: 'grandt',
    routesConfig: {
      baseURL: envConfig.routesBaseURL,
    },
    zondaIdentityConfig: {
      identityApiKey: zondaRemoteConfig.apiKey, // ,clarin: 'TjrTKBBbd9RYNQ9JjTtG5GunKUqnU0xR'
      identityURL: envConfig.identityURL,
      identityCallbackURL: envConfig.identityCallbackURL,
      googleClientId: 'XXXX.apps.googleusercontent.com', // TODO: placeholder, unconfirmed in the doc
    },
  };
}

let initialized = false;

/**
 * Returns whether zondaJS is actually initialized after this call. Callers
 * must check this before touching any other zondaJS method — the SDK's
 * behavior when called before init() is undocumented/unverified, so this is
 * defense-in-depth against any caller that doesn't already wait for
 * AuthenticatedZonda's config-readiness gate (see recessPosterAtom usage
 * there).
 */
export function initZonda(): boolean {
  if (initialized) {
    return true;
  }
  if (!window.zondaJS) {
    return false;
  }
  if (!zondaRemoteConfig.apiKey) {
    // eslint-disable-next-line no-console
    console.warn(
      'Zonda: apiKey not yet available from the backend config — skipping init.'
    );
    return false;
  }
  const zc = getZondaConfig();
  // eslint-disable-next-line no-console
  console.log('Zonda: initializing zondaJS with config', zc);
  window.zondaJS.init(zc);
  initialized = true;
  return true;
}

export async function zondaLogin() {
  if (!initZonda()) {
    return;
  }
  await window.zondaJS?.login();
}

export async function zondaLogout(clearSession = true) {
  if (!initZonda()) {
    return;
  }
  await window.zondaJS?.logout(clearSession);
}

export async function zondaSyncSession(forceLogin = false) {
  if (!initZonda()) {
    return undefined;
  }
  return window.zondaJS?.syncSession({ forceLogin });
}

export function zondaGetUserProfile() {
  if (!initialized) {
    return undefined;
  }
  return window.zondaJS?.getUserProfile();
}

export function zondaSetupGoogleOneTap() {
  // eslint-disable-next-line no-console
  // console.trace('disabled zondaSetupGoogleOneTap');
  // initZonda();
  // window.zondaJS?.setupGoogleOneTap();
}

/**
 * Confirmed by inspecting cookies while testing against QA: the SDK stores
 * the access token in a `zonda_access_token` cookie (not exposed via any
 * zondaJS method).
 */
export function zondaGetAccessToken(): string | undefined {
  return Cookies.get('zonda_access_token');
}

type ZondaAccessTokenParams = { accessToken: string };

export const sendZondaAccessToken = axiosErrorHandler(
  async ({ accessToken }: ZondaAccessTokenParams) => {
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    const form = new URLSearchParams();
    form.append('access_token', accessToken);

    const response = await axiosGdt.post<UserInitialData | GDTApiErrors>(
      `${URLs.LOGIN_ZONDA}&sitio=responsive&idPlataforma=${getPlatformId()}`,
      form,
      config
    );

    if (response.data.estado) {
      setCurrentCsrfToken(response.data.csrfToken, response.data.idUsuarioDT);
      return await augmentWithPrevTeam(response.data);
    } else {
      return response.data;
    }
  },
  TEXTS.initialDataError
);
